import React, { useEffect, useState, useCallback, useRef } from 'react';
import { debounce } from 'lodash';
import { useForm } from 'react-hook-form';
import BEM from '../../../../../common/bem';
import TCClient from '../../../../../client';
import { OidcFormInputField } from './OidcFormInputField';
import Button from 'admin/shared-components/Button';
import { Toast } from 'admin/shared-components/Toast/Toast';
import { actions, useAppDispatch, useAppSelector } from 'redux-stores';

const classes = BEM.with('AuthForm');

export type OidcFormBaseData = {
  oidcClientId: string;
  oidcClientSecret: string;
  oidcIssuerURL: string;
};

type IsManualConfig = { oidcIsManualConfig: boolean };

export type OidcFormFillableData = {
  oidcAuthorizationEndpoint?: string;
  oidcTokenEndpoint?: string;
  oidcUserInfoEndpoint?: string;
  oidcJwksEndpoint?: string;
};

export type OidcFormData = OidcFormBaseData & OidcFormFillableData & IsManualConfig;

const validateUrl = (url?: string) => {
  const errorMessage = 'Issuer URL needs to be a valid URL';
  if (!url?.length) {
    return true;
  }
  try {
    const urlObject = new URL(url);
    if (urlObject.protocol !== 'https:') {
      return errorMessage;
    }
    return true;
  } catch (e) {
    return errorMessage;
  }
};

export const OidcComponent: React.FC<{
  setFormIsValid: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ setFormIsValid }) => {
  const { organization } = useAppSelector((state) => ({
    organization: state.admin.selectedOrganization,
  }));
  const dispatch = useAppDispatch();

  const [warnings, setWarnings] = useState<Array<string>>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [toast, setToast] = useState<{
    isOpen: boolean;
    message: string;
    type: 'SUCCESS' | 'FAILURE';
  }>({
    isOpen: false,
    message: '',
    type: 'SUCCESS',
  });
  const {
    control,
    watch,
    formState: { errors, isValid },
    setValue,
    setError,
  } = useForm<OidcFormData>({
    defaultValues: {
      oidcClientId: '',
      oidcClientSecret: '',
      oidcIssuerURL: '',
      oidcIsManualConfig: false,
    },
    mode: 'onChange',
  });

  useEffect(() => {
    return () => {
      setFormIsValid(true);
    };
  }, [setFormIsValid]);

  useEffect(() => {
    setFormIsValid(isValid);
  }, [isValid, setFormIsValid]);

  const fillableInputsAreDisabled = !watch('oidcIsManualConfig');
  const issuerUrl = watch('oidcIssuerURL');

  const debouncedDispatchRef = useRef(
    debounce((fieldName: keyof OidcFormData, value: string) => {
      dispatch(actions.updateSettings({ [fieldName]: value }));
    }, 300)
  );

  const handleChange = useCallback((fieldName: keyof OidcFormData, value: string) => {
    debouncedDispatchRef.current(fieldName, value);
  }, []);

  useEffect(() => {
    const debouncedFn = debouncedDispatchRef.current;
    return () => {
      debouncedFn.cancel();
    };
  }, []);

  const validateIssuerUrl = useCallback(async () => {
    setIsValidating(true);
    setWarnings([]);
    try {
      const res = await TCClient.adminOrganizations.validateIssuerUrl({
        organizationId: organization.token,
        issuerUrl,
      });
      if (!res.errors?.length) {
        if (res.authorizationEndpoint?.length) {
          setValue('oidcAuthorizationEndpoint', res.authorizationEndpoint);
          dispatch(
            actions.updateSettings({
              oidcAuthorizationEndpoint: res.authorizationEndpoint,
            })
          );
        }
        if (res.tokenEndpoint?.length) {
          setValue('oidcTokenEndpoint', res.tokenEndpoint);
          dispatch(
            actions.updateSettings({
              oidcTokenEndpoint: res.tokenEndpoint,
            })
          );
        }
        if (res.userInfoEndpoint?.length) {
          setValue('oidcUserInfoEndpoint', res.userInfoEndpoint);
          dispatch(
            actions.updateSettings({
              oidcUserInfoEndpoint: res.userInfoEndpoint,
            })
          );
        }
        if (res.jwksEndpoint?.length) {
          setValue('oidcJwksEndpoint', res.jwksEndpoint);
          dispatch(
            actions.updateSettings({
              oidcJwksEndpoint: res.jwksEndpoint,
            })
          );
        }
      } else {
        setError('oidcIssuerURL', { message: res.errors[0], type: 'custom' });
      }
      if (res.warnings?.length) {
        setWarnings(res.warnings);
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: 'There was an Error validating the OIDC data, try again later.',
        type: 'FAILURE',
      });
      console.error('Error validating Issuer URL: ', error); // Sentry logger usage not needed
    } finally {
      setIsValidating(false);
    }
  }, [issuerUrl, organization.token, setValue, dispatch, setError]);

  return (
    <div className={classes()}>
      {toast.isOpen && (
        <Toast
          message={toast.message}
          type={toast.type}
          open={toast.isOpen}
          onClose={() => {
            setToast({ isOpen: false, message: '', type: 'SUCCESS' });
          }}
          autoHideDuration={2000}
        />
      )}
      <form>
        <div className="form-field">
          <OidcFormInputField
            name="oidcClientId"
            label="Client ID"
            type="text"
            control={control}
            errors={errors}
            rules={{ required: 'Client ID is required.' }}
            onChange={(value) => handleChange('oidcClientId', value)}
          />
        </div>
        <div className="form-field">
          <OidcFormInputField
            name="oidcClientSecret"
            label="Client Secret"
            type="password"
            control={control}
            errors={errors}
            rules={{ required: 'Client Secret is required.' }}
            onChange={(value) => handleChange('oidcClientSecret', value)}
          />
        </div>

        <div className="form-field">
          <OidcFormInputField
            name="oidcIssuerURL"
            label="Issuer URL"
            type="text"
            control={control}
            errors={errors}
            rules={{
              required: 'Issuer URL is required.',
              validate: validateUrl,
            }}
            onChange={(value) => handleChange('oidcIssuerURL', value)}
          />
          {warnings.length
            ? warnings.map((warning) => (
                <span key={warning} className="warning">
                  {warning}
                </span>
              ))
            : undefined}
          <div>
            <Button
              variant="outlined"
              onClick={validateIssuerUrl}
              type="button"
              disabled={!issuerUrl || !!errors.oidcIssuerURL || isValidating}
            >
              {isValidating ? 'Validating...' : 'Validate'}
            </Button>
          </div>
        </div>
        {/* 
        TODO: element will be enabled on an upcoming PR
        <div className="form-field">
          <label htmlFor="oidcIsManualConfig">
            Enable Manual endpoint configuration for non-standard providers
          </label>
          <Controller
            name="oidcIsManualConfig"
            control={control}
            render={({ field }) => (
              <Switch
                {...field}
                checked={field.value}
                onSwitch={() => {
                  field.onChange(!field.value);
                  handleChange('oidcIsManualConfig', `${!field.value}`);
                }}
              />
            )}
          />
        </div> */}
        <div className="form-field">
          <OidcFormInputField
            name="oidcAuthorizationEndpoint"
            label="Authorization Endpoint"
            type="text"
            disabled={fillableInputsAreDisabled}
            control={control}
            errors={errors}
            onChange={(value) => handleChange('oidcAuthorizationEndpoint', value)}
          />
        </div>
        <div className="form-field">
          <OidcFormInputField
            name="oidcTokenEndpoint"
            label="Token Endpoint"
            type="text"
            disabled={fillableInputsAreDisabled}
            control={control}
            errors={errors}
            onChange={(value) => handleChange('oidcTokenEndpoint', value)}
          />
        </div>
        <div className="form-field">
          <OidcFormInputField
            name="oidcUserInfoEndpoint"
            label="User Info Endpoint"
            type="text"
            disabled={fillableInputsAreDisabled}
            control={control}
            errors={errors}
            onChange={(value) => handleChange('oidcUserInfoEndpoint', value)}
          />
        </div>

        <div className="form-field">
          <OidcFormInputField
            name="oidcJwksEndpoint"
            label="JWKS Endpoint"
            type="text"
            disabled={fillableInputsAreDisabled}
            control={control}
            errors={errors}
            onChange={(value) => handleChange('oidcJwksEndpoint', value)}
          />
        </div>
      </form>
    </div>
  );
};
